import { useContext, useMemo } from 'react';

// import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { StateContext } from '@store/store';
// import { replaceTripleBraces } from '@utils/pricing.utils';
import {
  // convertGenerativeCreditsToTime,
  getAiCreditsFromCounter,
  getPricingValueFromCredits,
  secondsToMinutes,
  useAiCredits,
} from '../AiCredits';
import { UsageFeature, UsageQuota } from 'src/types/pricing.types';
import FeatureIcon from './FeatureIcons';

interface FeatureProps {
  addOnQuota?: UsageQuota;
  usageQuota: UsageQuota;
  planKey: string;
  feature: UsageFeature;
  totalGenerativeCredits: number;
}

const extractPlaceholders = (str: string): string[] => {
  const regex = /{{{([^}]+)}}}/g;
  const matches = str.match(regex);

  if (!matches) return [];

  return matches.map(match => match.slice(3, -3));
};

const convertStorage = (storage: number): string => {
  if (storage < 1000) {
    return `${storage} GB`;
  }

  return `${(storage / 1000).toFixed(0)} TB`;
};

const Feature = (props: FeatureProps) => {
  const { feature, planKey, usageQuota, totalGenerativeCredits, addOnQuota } =
    props;

  const globalState = useContext(StateContext);

  const { seats, aiCreditsCounter } = globalState;

  const multiplier = seats[planKey];

  const sanitizedLabel = useMemo(() => {
    let finalLabel = feature.description;

    if (feature.id === 'additional_features') {
      if (planKey === 'team') {
        finalLabel = finalLabel.replace(
          '{{{user_count}}}',
          `${multiplier} ${multiplier > 1 ? 'seats' : 'seat'}`,
        );
      } else {
        finalLabel = finalLabel.replace(
          '{{{user_count}}}',
          `${multiplier} ${multiplier > 1 ? 'users' : 'user'}`,
        );
      }
      finalLabel;
    }

    const placeholders = extractPlaceholders(finalLabel);

    placeholders.forEach(placeholder => {
      let addQuota = 0;
      if (addOnQuota) {
        addQuota =
          (addOnQuota[placeholder as keyof UsageQuota] ?? 0) *
          aiCreditsCounter[planKey];
      }

      const finalQuota =
        (usageQuota[placeholder as keyof UsageQuota] ?? 0) * multiplier +
        addQuota;

      switch (placeholder) {
        case 'ai_credits': {
          const value = getPricingValueFromCredits(
            totalGenerativeCredits,
            'ai_credits',
          );
          let finalValue: string | number = value;
          const seconds = value;
          if (seconds === 0) {
            finalValue = 'No generative videos';
          } else if (seconds > 600) {
            finalValue =
              secondsToMinutes(seconds).toString() +
              ' mins of generative videos';
          } else {
            finalValue = seconds.toString() + ' secs of generative video';
          }

          finalLabel = finalLabel.replace('{{{ai_credits}}}', finalValue);
          break;
        }
        case 'ugc_ads': {
          if (planKey === 'generative' || planKey === 'team') {
            const value = getPricingValueFromCredits(
              totalGenerativeCredits / 4,
              placeholder,
            );
            if (value === 0) {
              finalLabel = 'No UGC ads';
            } else if (value === 1) {
              finalLabel = `${value} generative UGC ad`;
            } else {
              finalLabel = `${value} generative UGC ads`;
            }

            finalLabel = finalLabel.replace(
              '{{{ugc_ads}}}',
              `${value} UGC product asset ads`,
            );
            break;
          }

          const value = getPricingValueFromCredits(
            totalGenerativeCredits,
            'ugc_ads',
          );
          finalLabel = finalLabel.replace(
            '{{{ugc_ads}}}',
            `${value} UGC product asset ads`,
          );
          break;
        }
        case 'ai_credits_v2': {
          finalLabel = finalLabel.replace(
            '{{{ai_credits_v2}}}',
            totalGenerativeCredits.toString(),
          );
          break;
        }
        case 'storage': {
          finalLabel = finalLabel.replace(
            `{{{${placeholder}}}}GB`,
            convertStorage(finalQuota),
          );
          break;
        }
        default: {
          finalLabel = finalLabel.replace(
            `{{{${placeholder}}}}`,
            finalQuota.toString(),
          );
          break;
        }
      }
    });

    return finalLabel;
  }, [
    feature.description,
    usageQuota,
    multiplier,
    feature.id,
    totalGenerativeCredits,
    addOnQuota,
    aiCreditsCounter,
    planKey,
  ]);

  return (
    <div
      className={ivclass(
        'iv-flex iv-items-start',
        'iv-relative',
        'iv-pb-[10px]',
      )}
    >
      <span className={ivclass('iv-pr-9 lg:iv-pr-[11px]')}>
        {/* <ImageComponent
          elementype="native"
          // src={feature.icon}
          loading="lazy"
          className={ivclass('iv-inline-block')}
        /> */}
        <FeatureIcon type={feature.type} />
      </span>
      <div
        className={ivclass(
          'iv-grow',
          'iv-text-black-85',
          'iv-font-medium',
          'iv-text-body-xl',
          planKey === 'free' && 'iv-opacity-50',
        )}
      >
        {/* {replaceTripleBraces(feature.description, { quota: finalQuota })} */}
        {sanitizedLabel}
      </div>
      {/* {help && platform === PlatformType.Desktop && (
        <div
          className={ivclass('iv-absolute iv-right-0 iv-top-0', 'sm:iv-hidden')}
        >
          <Tooltip content={help}>
            <ImageComponent
              elementype="native"
              src={`${assetsUrl}/pricing/info.svg`}
              loading="lazy"
              className={ivclass('iv-inline-block')}
            />
          </Tooltip>
        </div>
      )} */}
    </div>
  );
};

export default Feature;
